import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import type { ProductionWarehouse } from '../../types/production.types';

interface ProductionOrderFiltersProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (value: Date | null) => void;
  onDateToChange: (value: Date | null) => void;
  warehouseId: string | null;
  warehouses: ProductionWarehouse[];
  onWarehouseChange: (value: string | null) => void;
  rootOnly: boolean;
  onRootOnlyChange: (value: boolean) => void;
  filtersDirty: boolean;
  onClearFilters: () => void;
  showOpsStatuses?: boolean;
}

const ProductionOrderFilters: React.FC<ProductionOrderFiltersProps> = ({
  searchTerm,
  onSearchTermChange,
  statusFilter,
  onStatusFilterChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  warehouseId,
  warehouses,
  onWarehouseChange,
  rootOnly,
  onRootOnlyChange,
  filtersDirty,
  onClearFilters,
  showOpsStatuses,
}) => {
  const { t, i18n } = useTranslation('production');
  const dateLocale = i18n.language === 'en' ? enLocale : trLocale;

  return (
    <Box sx={{ mb: 2 }}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('orders.searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('orders.columns.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('orders.columns.status')}
                onChange={(event) => onStatusFilterChange(event.target.value)}
              >
                <MenuItem value="all">{t('orders.allStatuses')}</MenuItem>
                <MenuItem value="DRAFT">{t('status.DRAFT')}</MenuItem>
                {showOpsStatuses ? <MenuItem value="RELEASED">{t('status.RELEASED')}</MenuItem> : null}
                {showOpsStatuses ? <MenuItem value="IN_PROGRESS">{t('status.IN_PROGRESS')}</MenuItem> : null}
                <MenuItem value="COMPLETED">{t('status.COMPLETED')}</MenuItem>
                <MenuItem value="CANCELLED">{t('status.CANCELLED')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <DatePicker
              label={t('orders.dateFrom')}
              value={dateFrom}
              onChange={onDateFromChange}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <DatePicker
              label={t('orders.dateTo')}
              value={dateTo}
              onChange={onDateToChange}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('orders.warehouse')}</InputLabel>
              <Select
                value={warehouseId ?? ''}
                label={t('orders.warehouse')}
                onChange={(event) => onWarehouseChange(event.target.value || null)}
              >
                <MenuItem value="">{t('orders.allWarehouses')}</MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControlLabel
              control={<Switch checked={rootOnly} onChange={(event) => onRootOnlyChange(event.target.checked)} />}
              label={t('orders.rootOnly')}
            />
          </Grid>
          {filtersDirty && (
            <Grid item xs={12} sm="auto">
              <Button size="small" color="error" onClick={onClearFilters}>
                {t('orders.clearFilters')}
              </Button>
            </Grid>
          )}
        </Grid>
      </LocalizationProvider>
    </Box>
  );
};

export default ProductionOrderFilters;
